/* jshint esversion: 6 */
/* jshint ignore:start */
import React, { useState, useEffect } from "react";
import OneSignal from "react-onesignal";
import { IoIosNotifications, IoIosNotificationsOutline } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { showTost } from "../showTost";
import { useDraggableWidget } from "../hooks/useDraggableWidget";

const NotificationBellWidget = () => {
  const [permission, setPermission] = useState("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [tooltip, setTooltip] = useState(false);
  const [isWidgetDismissed, setIsWidgetDismissed] = useState(() => {
    return sessionStorage.getItem("hide_floating_notif") === "true";
  });

  const { position, bindPointer, wasDragged } = useDraggableWidget("floating_notif_pos", { bottom: 90, right: 24 }, 48, 48);

  const checkStatus = () => {
    try {
      if (typeof window !== "undefined" && window.OneSignal) {
        const currentPerm = OneSignal.Notifications?.permission ? "granted" : (Notification.permission || "default");
        setPermission(currentPerm);
        const subscribed = OneSignal.User?.PushSubscription?.optedIn ?? (currentPerm === "granted");
        setIsSubscribed(Boolean(subscribed));
      } else if (typeof Notification !== "undefined") {
        setPermission(Notification.permission || "default");
        setIsSubscribed(Notification.permission === "granted");
      }
    } catch (e) {
      console.warn("Error checking notification status:", e);
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = async () => {
    try {
      if (typeof window !== "undefined" && window.OneSignal) {
        if (OneSignal.Notifications?.requestPermission) {
          await OneSignal.Notifications.requestPermission();
        } else if (OneSignal.Slidedown?.promptPush) {
          await OneSignal.Slidedown.promptPush();
        }
      } else if (typeof Notification !== "undefined" && Notification.requestPermission) {
        await Notification.requestPermission();
      }
      checkStatus();
      showTost({ title: "Push Notification Request Triggered!" });
    } catch (err) {
      console.warn("Notification subscribe error:", err);
      showTost({ title: "Unable to prompt notification permission." });
    }
  };

  if (isWidgetDismissed) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: "none",
        userSelect: "none",
        zIndex: 9998,
      }}
      className="flex items-center gap-2"
      onMouseEnter={() => setTooltip(true)}
      onMouseLeave={() => setTooltip(false)}
    >
      {tooltip && (
        <div
          className={`absolute bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-fade-in flex items-center gap-1.5 ${
            position.y < 200 ? "top-14" : "-top-10"
          } ${position.x < 200 ? "left-0" : "right-0"}`}
        >
          {isSubscribed ? (
            <>
              <FaCheckCircle className="text-green-400 text-sm" />
              <span>Notifications Subscribed</span>
            </>
          ) : (
            <>
              <FaExclamationCircle className="text-yellow-400 text-sm" />
              <span>Click to Subscribe to Push Notifications</span>
            </>
          )}
        </div>
      )}

      <div className="relative group">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            sessionStorage.setItem("hide_floating_notif", "true");
            setIsWidgetDismissed(true);
          }}
          className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-gray-900/90 hover:bg-red-500 text-white rounded-full flex items-center justify-center shadow-md border border-white z-20 cursor-pointer transition-colors"
          title="Hide widget until next login"
        >
          <IoClose size={12} />
        </button>

        <button
          type="button"
          {...bindPointer}
          onClick={(e) => {
            if (wasDragged()) return;
            handleSubscribe();
          }}
          className={`relative w-12 h-12 rounded-full flex items-center justify-center text-white shadow-xl transition-transform duration-200 transform hover:scale-110 active:scale-95 cursor-grab active:cursor-grabbing ${
            isSubscribed
              ? "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              : "bg-gradient-to-r from-[#9610FF] to-pink-600 hover:from-purple-700 hover:to-pink-700 animate-pulse"
          }`}
          aria-label="Push Notification Bell"
        >
          {isSubscribed ? (
            <IoIosNotifications className="text-2xl" />
          ) : (
            <IoIosNotificationsOutline className="text-2xl text-white" />
          )}

          {/* Status indicator dot */}
          <span
            className={`absolute top-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${
              isSubscribed ? "bg-green-500" : "bg-yellow-400 animate-ping"
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default NotificationBellWidget;
/* jshint ignore:end */
